// ============================================================
// THE ACTION ECONOMY - advisory, never enforcing.
//
// A turn is two points: a Full action spends both, a Half spends one, a
// Free spends none. The app COUNTS them and shows the count. It does not
// stop you, because the user's tables let players exceed a Full action
// and a simulator that argues with the GM is worse than one that stays
// quiet.
//
// A Reaction costs nothing here on purpose. Reactions are not part of
// the turn's budget - they happen on somebody else's - and counting them
// would make the pips lie about what is left to do.
//
// Pure. Zero React dependency.
// ============================================================
import type { ActionCost, ActionDef } from './types';

/** Points in a turn. Two, and the pips draw two. */
export const TURN_POINTS = 2;

export const COST_POINTS: Record<ActionCost, number> = {
  free: 0,
  half: 1,
  full: 2,
  // A Half-or-Full action is charged as the Half it usually is. The
  // alternative is to ask, and a readout nobody asked for should not
  // interrupt to be accurate about a number nobody is enforcing.
  halfOrFull: 1,
  reaction: 0,
};

export interface EconomyState {
  spent: number;
  total: number;
  /** Points spent beyond the turn's budget - 0 when within it. */
  over: number;
  /** Pips to draw: `total` of them, the first `filled` lit. */
  filled: number;
}

export function economyOf(spent: number): EconomyState {
  const s = Math.max(0, spent || 0);
  return {
    spent: s,
    total: TURN_POINTS,
    over: Math.max(0, s - TURN_POINTS),
    filled: Math.min(s, TURN_POINTS),
  };
}

/**
 * Is the turn spent - should END TURN suggest itself?
 *
 * "Spent" includes OVERSPENT. The economy counts and never enforces, so
 * a third point on a two-point turn is still a turn worth ending - the
 * button went dark again past the budget until v8.331.4, which read as
 * the app changing its mind about what it had just suggested.
 */
export const turnSpent = (state: EconomyState): boolean => state.spent >= state.total;

/**
 * What a RELOAD costs the turn, from the weapon's own reload text -
 * "Half", "Full", "2 Full" - as typed on the sheet. The reload is not a
 * catalog action (it is a key and a footer button on the weapon), so it
 * charged nothing until v8.331.4, and a Full-action reload left both
 * pips unlit. Anything that is not a Half or a Full - "N/A", "Free",
 * blank - costs nothing.
 */
export function reloadPoints(reloadAction: string | null | undefined): number {
  const m = /^\s*(\d+)?\s*(half|full)\s*$/i.exec(reloadAction ?? '');
  if (!m) return 0;
  const times = m[1] ? Number(m[1]) : 1;
  return times * (m[2].toLowerCase() === 'full' ? COST_POINTS.full : COST_POINTS.half);
}

/** What this action costs the turn. */
export const pointsFor = (cost: ActionCost): number => COST_POINTS[cost] ?? 0;

/**
 * One line for the readout's tooltip - and the only place the overspend
 * is WORDED, so the phrasing cannot drift between the pips and the log.
 */
export function describeEconomy(state: EconomyState): string {
  if (state.over > 0) {
    return `${state.spent} of ${state.total} action points spent - ${state.over} past a full turn. Advisory only.`;
  }
  const left = state.total - state.spent;
  if (left === 0) return `${state.spent} of ${state.total} action points spent - the turn is full. Advisory only.`;
  return `${state.spent} of ${state.total} action points spent. Advisory only.`;
}

/**
 * What pressing this plate costs the turn - which is NOT always its
 * cost (v8.309.2).
 *
 * An `engage` action is a TOGGLE, and it was charged like an event:
 * every press added, so Aim on-off-on cost three Half actions and the
 * count grew without limit while a player made up their mind. Putting a
 * stance down is not a second action, so the press that puts it down
 * REFUNDS the one that took it up.
 *
 * `engageAndFire` and `instant` are not toggles - each press is another
 * swing, or another thing done - so each of those pays in full. Pressing
 * All-Out twice really is two All-Out Attacks.
 *
 * Chambering costs nothing at all: it is intent, and the trigger pays.
 *
 * @param held Whether the stance is engaged RIGHT NOW, before the press.
 */
export function chargeForPress(def: ActionDef, held: boolean): number {
  if (def.execution === 'chamber') return 0;
  const cost = pointsFor(def.cost);
  if (def.execution !== 'engage') return cost;
  return held ? -cost : cost;
}
